<template>
  <div class="max-w-6xl mx-auto px-6 py-8">
    <!-- Loading State -->
    <div v-if="isLoading" class="flex items-center justify-center py-20">
      <div class="text-center">
        <div
          class="w-12 h-12 border-4 border-violet-200 border-t-violet-600 rounded-full animate-spin mx-auto mb-4"
        ></div>
        <p class="text-gray-500">Loading trends...</p>
      </div>
    </div>

    <!-- Empty State -->
    <div
      v-else-if="trendingTopics.length === 0"
      class="flex flex-col items-center justify-center text-center py-20 bg-white rounded-lg border border-gray-200"
    >
      <div
        class="text-gray-400 mb-4 flex justify-center"
        v-html="TRENDS_EMPTY_ICON"
      ></div>
      <h3 class="text-lg font-medium text-gray-900 mb-2">
        No trending topics yet
      </h3>
      <p class="text-gray-500 max-w-md mx-auto">
        As more calls are processed, you'll see trending topics and themes
        emerge here. Start by searching for specific topics.
      </p>
      <button
        @click="goToSearch"
        class="mt-6 px-6 py-2.5 bg-violet-600 text-white rounded-lg hover:bg-violet-700 transition-colors"
      >
        Search Calls
      </button>
    </div>

    <!-- Trends Table -->
    <div
      v-else
      class="bg-white rounded-lg border border-gray-200 overflow-hidden"
    >
      <table class="w-full">
        <thead>
          <tr class="border-b border-gray-200">
            <th
              class="text-left px-6 py-4 text-sm font-semibold text-gray-900 whitespace-nowrap"
            >
              Topics
            </th>
            <th
              class="text-left px-6 py-4 text-sm font-semibold text-gray-900 whitespace-nowrap"
            >
              Last Mentioned
            </th>
            <th
              class="text-left px-6 py-4 text-sm font-semibold text-gray-900 whitespace-nowrap"
            >
              Experts
            </th>
            <th
              class="text-left px-6 py-4 text-sm font-semibold text-gray-900 whitespace-nowrap"
            >
              <span class="inline-flex items-center gap-1.5">
                Topic Trend
                <span
                  class="relative inline-flex items-center group cursor-help shrink-0"
                >
                  <span
                    v-html="INFO_ICON"
                    class="inline-flex w-4 h-4 shrink-0 text-gray-400 [&_svg]:w-full [&_svg]:h-full"
                  ></span>
                  <div
                    class="absolute right-0 top-full mt-1 px-2 py-1.5 text-xs text-white bg-gray-900 rounded-md opacity-0 group-hover:opacity-100 transition-opacity shadow-lg z-50 pointer-events-none w-48 text-left flex flex-col gap-0.5"
                  >
                    <div
                      v-for="(line, i) in TOPIC_TREND_TOOLTIP_LINES"
                      :key="i"
                    >
                      {{ line }}
                    </div>
                  </div>
                </span>
              </span>
            </th>
          </tr>
        </thead>
        <tbody class="divide-y divide-gray-100">
          <tr
            v-for="topic in trendingTopics"
            :key="topic.topic"
            class="hover:bg-gray-50 transition-colors"
          >
            <td class="px-6 py-4 text-left">
              <span class="font-medium text-gray-900">{{ topic.topic }}</span>
            </td>
            <td class="px-6 py-4 text-left">
              <span class="text-sm text-gray-500">
                {{ formatLastMentioned(topic) }}
              </span>
            </td>
            <td class="px-6 py-4 text-left">
              <span class="text-sm text-gray-700">
                {{ formatExperts(topic.expert_names) }}
              </span>
              <span
                v-if="topic.expert_names.length > 2"
                class="relative inline-block ml-1 group"
              >
                <span class="text-sm text-violet-500 cursor-default">
                  +{{ topic.expert_names.length - 2 }} more
                </span>
                <div
                  class="absolute left-0 top-full mt-1 px-2 py-1.5 text-xs text-white bg-gray-900 rounded-md opacity-0 group-hover:opacity-100 transition-opacity shadow-lg z-50 pointer-events-none min-w-max"
                >
                  <div class="flex flex-col gap-0.5">
                    <span
                      v-for="name in topic.expert_names.slice(2)"
                      :key="name"
                    >
                      {{ name }}
                    </span>
                  </div>
                </div>
              </span>
            </td>
            <td class="px-6 py-4 text-left align-middle">
              <div class="w-24 h-10 flex items-center justify-center">
                <TrendSparkline
                  :data="topic.trend_data"
                  @click="openTrendModal(topic)"
                />
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Trend detail modal -->
    <TrendDetailModal :topic="selectedTopic" @close="selectedTopic = null" />
  </div>
</template>

<script lang="ts">
import { defineComponent, computed, onMounted, ref } from "vue";
import { useRouter } from "vue-router";
import { useQuery } from "@vue/apollo-composable";
import gql from "graphql-tag";
import TrendSparkline from "@/components/insights/TrendSparkline.vue";
import {
  INFO_ICON,
  TRENDS_EMPTY_ICON,
} from "@/assets/svg/shared/svg-constants";
import TrendDetailModal from "@/components/insights/TrendDetailModal.vue";
import { useInsightsStore, TrendingTopic } from "@/stores/insights";
import { useUserStore } from "@/stores/user";

const GET_TRENDING_TOPICS_QUERY = gql`
  query GetProgramTrendingTopics($programId: ID!, $limit: Int) {
    programTrendingTopics(programId: $programId, limit: $limit) {
      topic
      count
      startedAt
      trendData {
        processedDate
        count
      }
      expertNames
      bookingIds
    }
  }
`;

const TOPIC_TREND_TOOLTIP_LINES = [
  "Click any sparkline to view",
  "the full daily mentions graph.",
];

function mapApiTopicToStore(apiTopic: {
  topic: string;
  count: number;
  startedAt: string | null;
  trendData: Array<{ processedDate: string; count: number }>;
  expertNames: string[];
  bookingIds: string[];
}): TrendingTopic {
  return {
    topic: apiTopic.topic,
    count: apiTopic.count ?? 0,
    started_at: apiTopic.startedAt ?? null,
    trend_data: (apiTopic.trendData ?? []).map((d) => ({
      processed_date: d.processedDate,
      count: d.count ?? 0,
    })),
    expert_names: apiTopic.expertNames ?? [],
    booking_ids: apiTopic.bookingIds ?? [],
  };
}

export default defineComponent({
  name: "InsightsTrendsView",
  components: {
    TrendSparkline,
    TrendDetailModal,
  },
  setup() {
    const router = useRouter();
    const insightsStore = useInsightsStore();
    const userStore = useUserStore();

    const TRENDS_TOP_LIMIT = 10;
    const programId = computed(() => userStore.selectedProgram?.id ?? null);
    const isQueryEnabled = computed(() => !!programId.value);

    const { result, loading } = useQuery(
      GET_TRENDING_TOPICS_QUERY,
      () => ({ programId: programId.value ?? "", limit: TRENDS_TOP_LIMIT }),
      {
        fetchPolicy: "network-only",
        enabled: isQueryEnabled,
      }
    );

    type ApiTopic = Parameters<typeof mapApiTopicToStore>[0];
    const trendingTopics = computed<TrendingTopic[]>(() => {
      const topics = result.value?.programTrendingTopics ?? [];
      return topics.map((t: ApiTopic) => mapApiTopicToStore(t));
    });

    const isLoading = computed(() => loading.value);
    const selectedTopic = ref<TrendingTopic | null>(null);

    const openTrendModal = (topic: TrendingTopic) => {
      selectedTopic.value = topic;
    };

    const goToSearch = () => {
      router.push({ name: "InsightsSearch" });
    };

    const formatLastMentioned = (topic: TrendingTopic): string => {
      const dates = topic.trend_data
        .map((d) => d.processed_date)
        .sort()
        .reverse();
      const lastDate = dates[0]; // Last mentioned = most recent date in trend_data
      if (!lastDate) return "—";
      const todayUtc = new Date().toISOString().slice(0, 10);
      if (lastDate === todayUtc) return "Today";
      const last = new Date(lastDate + "T12:00:00Z");
      const todayNoon = new Date(todayUtc + "T12:00:00Z");
      const diffDays = Math.round(
        (todayNoon.getTime() - last.getTime()) / (1000 * 60 * 60 * 24)
      );
      if (diffDays === 1) return "1 day ago";
      return `${diffDays} days ago`;
    };

    const formatExperts = (names: string[]): string => {
      if (!names.length) return "—";
      return names.slice(0, 2).join(", ");
    };

    onMounted(() => {
      insightsStore.setActiveTab("trends");
    });

    return {
      TRENDS_EMPTY_ICON,
      TOPIC_TREND_TOOLTIP_LINES,
      INFO_ICON,
      isLoading,
      trendingTopics,
      selectedTopic,
      openTrendModal,
      goToSearch,
      formatLastMentioned,
      formatExperts,
    };
  },
});
</script>
